import React, { Fragment, useEffect, useState } from "react";

import {
  Box,
  Flex,
  Icon,
  Select,
  Spinner,
  Stack,
  Text,
} from "@storyofams/react-ui";
import { useRouter } from "next/router";
import { usePostHog } from "posthog-js/react";
import { Button } from "~components/common/Button";
import { Search, Sort, Star } from "~components/common/Icons";
import useMediaQuery from "~hooks/useMediaQuery";
import { StyledInput } from "../Questions";
import { Stars } from "../Stars";
import { Review } from "./Review";
import { ReviewForm } from "./ReviewForm";

const sorting = [
  { value: "videosFileName", asc: false, label: "Meest relevant" },
  { value: "rating", asc: false, label: "Hoogste beoordeling" },
  { value: "rating", asc: true, label: "Laagste beoordeling" },
  { value: "voteRating", asc: false, label: "Meeste stemmen" },
  { value: "created_at", asc: false, label: "Nieuw - oud" },
];

export const Reviews = ({
  loadingReviews,
  reviews,
  propertys,
  skinType,
  age,
  purposeState,
  starsFilter,
  starsMemo,
  score,
  skinTypeMemo,
  purposeMemo,
  productTitle,
  searchMemo,
  bodyPartMemo,
  bodyPart,
  searchState,
  ageMemo,
  setFilterMemo,
  sortingState,
  productId,
  children,
}) => {
  const { query, isReady } = useRouter();
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const posthog = usePostHog();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const stars = [
    { value: 5, label: <StarsSelect count={0} /> },
    { value: 4, label: <StarsSelect count={1} /> },
    { value: 3, label: <StarsSelect count={2} /> },
    { value: 2, label: <StarsSelect count={3} /> },
    { value: 1, label: <StarsSelect count={4} /> },
  ];
  useEffect(() => {
    if (query?.form === "open") {
      const el = document?.getElementById("form");
      if (el) {
        setTimeout(
          () => el.scrollIntoView({ behavior: "smooth", block: "start" }),
          1000
        );
      }
    } else if (isReady && query?.form !== "open") setIsOpen(false);
  }, [query, isReady]);
  return (
    <>
      <Flex
        mt={["30px", 0]}
        alignItems="center"
        justifyContent={"space-between"}
        mb={2}
        flexDirection={["column", "row"]}
      >
        <Text fontFamily={"DINPro"} fontWeight={700} fontSize={"28px"}>
          Beoordelingen
        </Text>

        <Flex
          mb={1}
          pt={"5px"}
          alignItems="center"
          flexDirection={["column", "row"]}
        >
          <Flex mb={["10px", 0]}>
            <Stars isOverview={true} stars={Number(score?.average_rating)} />

            <Text fontSize={"20px"} fontWeight={500} mx={1}>
              {score?.review_count ? score?.average_rating : "No reviews"}
            </Text>
          </Flex>

          {!!score?.review_count && (
            <Text fontSize={"16px"} fontWeight={400}>
              {`Gebaseerd op ${score?.review_count} beoordelingen`}
            </Text>
          )}
        </Flex>

        <Button
          onClick={() => {
            setIsOpen((open) => !open);
            // posthog.capture("Clicked Reviews Widget", {
            //   section: "write a review button",
            // });

            if (!isOpen && isMobile)
              window.scrollBy({ top: 400, behavior: "smooth" });
          }}
          marginTop={2}
          marginBottom={2}
          fontSize={2.5}
          size="small"
          variant="primary"
        >
          Schrijf een review
        </Button>
      </Flex>
      {isOpen && (
        <ReviewForm
          propertys={propertys}
          productId={productId}
          closeModal={() => setIsOpen(false)}
          productTitle={productTitle}
        />
      )}
      <Box
        display={"flex"}
        justifyContent={"space-between"}
        mb={[1, 3]}
        flexWrap={"wrap"}
        flexDirection={["column", "row"]}
      >
        <Flex flexDirection={["column", "row"]}>
          <Select
            //@ts-ignore
            isClearable={true}
            mb={[1, 0]}
            value={starsFilter}
            onChange={(e) => {
              starsMemo(e);
              // posthog.capture("Clicked Reviews Widget", {
              //   section: "Minimum rating select",
              // });
            }}
            placeholder={<StarsSelect count={0} />}
            width={["100%", 170]}
            //@ts-ignore
            options={stars}
            openMenuOnFocus={true}
            css={`
              border: 1px solid #c1bebd;
              border-radius: 8px;
              height: 40px;
              min-height: 40px;
              & > div {
                border-radius: 7px !important;
                font-size: 16px;
                transition: border-color 0.18s ease-in-out,
                  background-color 0.18s ease-in-out;
              }
              &:not(:disabled) {
                &:hover,
                &:active,
                &:focus {
                  border-color: black;
                }
              }
              .react-select__dropdown-indicator {
                display: ${starsFilter && "none"};
              }
              .react-select__option {
                &:hover {
                  background: #f4f3f0;
                }
              }
            `}
          />
          <Select
            ml={[0, 1]}
            mb={[1, 0]}
            value={age}
            options={propertys?.ages}
            onChange={(e) => ageMemo(e)}
            width={["100%", 120]}
            isClearable={true}
            openMenuOnFocus={true}
            placeholder="Leeftijd"
            css={`
              border: 1px solid #c1bebd;
              border-radius: 8px;
              height: 40px;
              min-height: 40px;
              & > div {
                border-radius: 7px !important;
                font-size: 16px;
                transition: border-color 0.18s ease-in-out,
                  background-color 0.18s ease-in-out;
              }
              &:not(:disabled) {
                &:hover,
                &:active,
                &:focus {
                  border-color: black;
                }
              }
              .react-select__dropdown-indicator {
                display: ${age && "none"};
              }
              .react-select__option {
                padding: 3px 3px;
                &:hover {
                  background: #f4f3f0;
                }
              }
            `}
          />
          <Select
            ml={[0, 1]}
            mb={[1, 0]}
            value={skinType}
            onChange={(e) => skinTypeMemo(e)}
            options={propertys.skinTypes}
            isClearable={true}
            width={["100%", 120]}
            openMenuOnFocus={true}
            isSearchable={false}
            placeholder="Huidtype"
            css={`
              border: 1px solid #c1bebd;
              border-radius: 8px;
              height: 40px;
              min-height: 40px;
              & > div {
                border-radius: 7px !important;
                font-size: 16px;
                transition: border-color 0.18s ease-in-out,
                  background-color 0.18s ease-in-out;
              }
              &:not(:disabled) {
                &:hover,
                &:active,
                &:focus {
                  border-color: black;
                }
              }
              .react-select__dropdown-indicator {
                display: ${skinType && "none"};
              }
              .react-select__menu {
                width: 170px;
              }
              .react-select__option {
                padding: 3px 3px;
                &:hover {
                  background: #f4f3f0;
                }
              }
            `}
          />
          <Select
            ml={[0, 1]}
            mb={[1, 0]}
            value={purposeState}
            onChange={(e) => purposeMemo(e)}
            options={propertys.skinConditions}
            isClearable={true}
            width={["100%", 169]}
            openMenuOnFocus={true}
            placeholder="Huidaandoening"
            css={`
              border: 1px solid #c1bebd;
              border-radius: 8px;
              height: 40px;
              min-height: 40px;
              & > div {
                border-radius: 7px !important;
                font-size: 16px;
                transition: border-color 0.18s ease-in-out,
                  background-color 0.18s ease-in-out;
              }
              &:not(:disabled) {
                &:hover,
                &:active,
                &:focus {
                  border-color: black;
                }
              }
              .react-select__dropdown-indicator {
                display: ${purposeState && "none"};
              }
              .react-select__option {
                padding: 3px 3px;
                &:hover {
                  background: #f4f3f0;
                }
              }
            `}
          />
          <Select
            ml={[0, 1]}
            mb={[1, 0]}
            value={bodyPart}
            onChange={(e) => bodyPartMemo(e)}
            options={propertys.bodyParts}
            isClearable={true}
            width={["100%", 155]}
            openMenuOnFocus={true}
            placeholder="Lichaamsdeel"
            css={`
              border: 1px solid #c1bebd;
              border-radius: 8px;
              height: 40px;
              min-height: 40px;
              & > div {
                border-radius: 7px !important;
                font-size: 16px;
                transition: border-color 0.18s ease-in-out,
                  background-color 0.18s ease-in-out;
              }
              &:not(:disabled) {
                &:hover,
                &:active,
                &:focus {
                  border-color: black;
                }
              }
              .react-select__dropdown-indicator {
                display: ${purposeState && "none"};
              }
              .react-select__option {
                padding: 3px 3px;
                &:hover {
                  background: #f4f3f0;
                }
              }
            `}
          />
        </Flex>
        <Box position={"relative"} width={["100%", "300px"]}>
          <StyledInput
            as="input"
            px={2}
            py={1.5}
            pr={5}
            fontSize={2}
            height="40px"
            width={"100%"}
            color="black"
            borderRadius="md"
            type="search"
            name="search"
            // onClick={() =>
            //   posthog.capture("Clicked Questions Widget", {
            //     section: "search",
            //   })
            // }
            placeholder="Zoek binnen reviews"
            value={searchState}
            onChange={(e: any) => {
              searchMemo(e.target.value);
            }}
          />
          <Icon
            position="absolute"
            right={1.5}
            top={0}
            bottom={0}
            color="grey800"
            fontSize={2}
            icon={Search}
          />
        </Box>
      </Box>
      <Box display={"flex"} mb={[1, 3]} justifyContent={"end"}>
        <Box position={"relative"}>
          <Select
            value={sortingState}
            onChange={(e) => {
              setFilterMemo(e);
              // posthog.capture("Clicked Reviews Widget", {
              //   section: "Sorting select",
              // });
            }}
            width={213}
            height={40}
            options={sorting}
            placeholder="Selecteer je leeftijdsgroep"
            css={`
              border: 1px solid #c1bebd;
              border-radius: 8px;
              height: 40px;
              min-height: 40px;
              & > div {
                border-radius: 7px !important;
                font-size: 16px;

                transition: border-color 0.18s ease-in-out,
                  background-color 0.18s ease-in-out;
              }
              &:not(:disabled) {
                &:hover,
                &:active,
                &:focus {
                  border-color: black;
                }
              }
              .react-select__option {
                padding: 3px 3px;
                &:hover {
                  background: #f4f3f0;
                }
              }
              .react-select__value-container {
                padding-left: 30px;
              },
            `}
          />
          <Icon
            position="absolute"
            left={1.5}
            top={0}
            bottom={0}
            color="grey800"
            fontSize={2}
            icon={Sort}
          />
        </Box>
      </Box>
      {/* <Box
        width="100%"
        display={["block", "none"]}
        justifyContent="end"
        mb={2}
        alignItems="center"
      >
        <Box position={"relative"}>
          <StyledInput
            mb={1}
            as="input"
            px={2}
            py={1.5}
            pr={5}
            fontSize={2}
            height="40px"
            width={["100%", "200px", "220px"]}
            color="black"
            borderRadius="md"
            type="search"
            name="search"
            onClick={() =>
              posthog.capture("Clicked Questions Widget", {
                section: "search",
              })
            }
            placeholder="Zoek binnen reviews"
            value={searchState}
            onChange={(e: any) => {
              searchMemo(e.target.value);
            }}
          />
          <Icon
            position="absolute"
            right={1.5}
            top={-1}
            bottom={0}
            color="grey800"
            fontSize={2}
            icon={Search}
          />
        </Box>
        <Flex>
          <Box
            height={40}
            onClick={() => setIsFiltersOpen(true)}
            width="60%"
            justifyContent={"center"}
            display={"flex"}
            border={"1px solid grey400"}
            borderRadius={"md"}
            alignItems={"center"}
            mr="10px"
          >
            <Text mr={1.5} pb="1px">
              Filters
            </Text>
            <Icon icon={Filter}></Icon>
          </Box>
          <Box width={"100%"} position={"relative"}>
            <Select
              value={sortingState}
              onChange={(e) => {
                setFilterMemo(e);
                posthog.capture("Clicked Reviews Widget", {
                  section: "Sorting select",
                });
              }}
              height={40}
              options={sorting}
              placeholder="Selecteer je leeftijdsgroep"
              css={`
              border: 1px solid #c1bebd;
              border-radius: 8px;
              height: 40px;
              min-height: 40px;
              & > div {
                border-radius: 7px !important;
                font-size: 16px;
                transition: border-color 0.18s ease-in-out,
                  background-color 0.18s ease-in-out;
              }
              &:not(:disabled) {
                &:hover,
                &:active,
                &:focus {
                  border-color: black;
                }
              }
              .react-select__value-container {
                padding-left: 30px;
              },
            `}
            />
            <Icon
              position="absolute"
              left={1.5}
              top={0}
              bottom={0}
              color="grey800"
              fontSize={2}
              icon={Sort}
            />
          </Box>
        </Flex>
      </Box>
      <MobielFilters
        isOpen={isFiltersOpen}
        setIsOpen={setIsFiltersOpen}
        skinType={skinType}
        age={age}
        purposeState={purposeState}
        starsFilter={starsFilter}
        starsMemo={starsMemo}
        skinTypeMemo={skinTypeMemo}
        ageMemo={ageMemo}
        purposeMemo={purposeMemo}
      /> */}
      <Box flex={1}>
        <Stack
          space={4}
          flexDirection="column"
          // maxHeight={880}
          overflowY="auto"
        >
          {loadingReviews ? (
            <Flex
              minHeight={700}
              height="100%"
              width="100%"
              alignItems="center"
              justifyContent="center"
            >
              <Spinner />
            </Flex>
          ) : (
            reviews?.map((review, i) => (
              <Fragment key={review?.id}>
                <Review {...review} {...review.customer_profiles} />
              </Fragment>
            ))
          )}
          {!loadingReviews && !!!reviews?.length && <Text>No reviews</Text>}
        </Stack>

        {children}
      </Box>
    </>
  );
};
export const StarsSelect = ({ count }) => {
  return (
    <Box>
      {[1, 2, 3, 4, 5].slice(count).map((i) => (
        <Icon
          fontWeight={2}
          fontSize={"24px"}
          key={`star-${i}`}
          icon={Star}
          width={"16px"}
          color="oldPink"
          opacity={1}
        />
      ))}
    </Box>
  );
};
